import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError, StatusCode } from 'grpc-web';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, APIRequest, APIResponse, getSession, refreshSession } from '../../../app/Api';
import { formatDate, timestampToDate } from '../../../app/Helpers';

import { TableState } from '../../common/Table/TableSate';
import { number } from 'yup';
import { OrganizationEndpointClient } from '../../../repository/UserManagement/OrganizationServiceClientPb';
import { GetOrganizationsResponse, GetOrganizationsRequest, UpdateOrganizationActivityResponse, UpdateOrganizationStateRequest } from '../../../repository/UserManagement/organization_pb';


export interface OrganizationsPageState {
    organizations: TableState
    isChangeStateLoading: boolean,
    message?: ApiMessage,
    searchText: string | undefined,
    filters: any[]

}

const initialState: OrganizationsPageState = {
    organizations: { items: [], hasMore: true, isDescending: true, isFetching: false, numberOfResults: 10, selected: [], isFilteredSet: false },
    isChangeStateLoading: false,
    message: undefined,
    searchText: undefined,
    filters: []
}

const organizationClient = new OrganizationEndpointClient(API_URL, null, null);

export const getOrganizations = createAsyncThunk<GetOrganizationsResponse.AsObject, APIRequest<GetOrganizationsRequest>, {
    rejectValue: ApiMessage
}>(
    'pages/organizations/getOrganizations',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await organizationClient.getOrganizations(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {
                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)


export const updateStatus = createAsyncThunk<APIResponse<UpdateOrganizationActivityResponse.AsObject>, APIRequest<UpdateOrganizationStateRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/organizations/updateStatus',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await organizationClient.updateActivity(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId(), state: req.body.getIsactive() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }

)


const getFilters = (state: OrganizationsPageState): any[] => {


    return [];

}
export const organizationsPageSlice = createSlice({
    name: 'pages/organizations',
    initialState,
    reducers: {
        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.organizations = initialState.organizations;
            state.message = initialState.message;
            state.isChangeStateLoading = initialState.isChangeStateLoading;
            state.searchText = initialState.searchText;
        },
        resetOrganizations: (state) => {
            state.organizations.items = [];
            state.organizations.hasMore = true;
        },

        setSort: (state, action: PayloadAction<boolean>) => {
            state.organizations.isDescending = action.payload;
        },
        setNumberOfResults: (state, action: PayloadAction<number>) => {
            state.organizations.numberOfResults = action.payload;
        },
        setSelectedOrganization: (state, action: PayloadAction<any[]>) => {
            state.organizations.selected = action.payload;
        },
        setSearchText: (state, action: PayloadAction<string | undefined>) => {
            state.searchText = action.payload;
        },
        setIsFilteredSet: (state, action: PayloadAction<boolean>) => {
            state.organizations.isFilteredSet = action.payload;
        },
        addOrganization: (state, action: PayloadAction<any>) => {
            if (!state.organizations.isFilteredSet) {
                if (!state.organizations.hasMore && !state.organizations.isDescending) {
                    state.organizations.items.push(action.payload);
                } else if (state.organizations.isDescending) {
                    state.organizations.items.unshift(action.payload);
                }
            }
        },
        updateOrganization: (state, action: PayloadAction<any>) => {
            let selected = state.organizations.selected;
            if (action?.payload) {
                if (selected.length > 0 && selected.at(0).id == action?.payload?.id) {
                    selected[0] = action?.payload
                }
                var u = state.organizations.items.findIndex(e => e.id == action?.payload?.id)
                if (u >= 0) {
                    state.organizations.items[u] = action?.payload
                }
            }

        }
    },
    extraReducers: (builder) => {
        builder.addCase(getOrganizations.fulfilled, (state, { payload }) => {
            state.organizations.isFetching = false
            if (payload) {
                var r = (payload?.success?.organizationsList.map(val => {
                    return {
                        id: val.id,
                        name: (val.name?.value ?? ""),
                        domain: val.domain?.value ?? "",
                        isActive: val.isactive?.value,
                        createdDate: formatDate(timestampToDate(val.createddate?.seconds, val.createddate?.nanos)),
                        addedBy: (val.addedby?.firstname?.value ?? "") + " " + (val.addedby?.lastname?.value ?? ""),
                        addedById: val.addedby?.id?.value,
                    }
                }
                ) as any[])
                //r = (r.concat(...Array(9).fill(r)));
                let l = [];
                if (state.organizations.items.length != 0) {
                    l = state.organizations.items.slice(0, state.organizations.items.length - 10)
                }
                l = l.concat(r);
                if (r.length < state.organizations.numberOfResults) {
                    state.organizations.hasMore = false;
                } else {
                    l = l.concat(...Array(10).fill(null));
                }

                state.organizations.items = l
                //console.log(state.items)

            } else {
                state.organizations.hasMore = false;
            }
        })
        builder.addCase(getOrganizations.rejected, (state, action) => {
            state.organizations.isFetching = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(getOrganizations.pending, (state, action) => {
            state.organizations.isFetching = true;
            state.message = undefined;
        })


        builder.addCase(updateStatus.fulfilled, (state, { payload }) => {
            state.isChangeStateLoading = false;
            if (payload) {
                state.message = toApiMessage(payload?.response.success?.message);
                let selected = state.organizations.selected;
                if (payload?.metadata) {
                    if (selected.length > 0 && selected.at(0).id == payload?.metadata.id) {
                        selected.at(0).isActive = payload?.metadata.state
                    }
                    var u = state.organizations.items.findIndex(e => e.id == payload?.metadata.id)
                    if (u >= 0) {
                        state.organizations.items.at(u).isActive = payload?.metadata.state
                    }

                }
            }
        })
        builder.addCase(updateStatus.rejected, (state, action) => {
            state.isChangeStateLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(updateStatus.pending, (state, action) => {
            state.isChangeStateLoading = true;
            state.message = undefined;
        })





    }
})

export const { reset, dismissMessage, setSort, setNumberOfResults, resetOrganizations, setSelectedOrganization, setSearchText, setIsFilteredSet, addOrganization, updateOrganization } = organizationsPageSlice.actions

export default organizationsPageSlice.reducer


